import React, { useState } from "react";
import { BookOpen, Sparkles, X, Shield, Workflow } from "lucide-react";

const STORAGE_KEY = "chaos-guide-dismissed";

/**
 * Dismissible 3-step operator guide. When the user closes it, the panel
 * collapses down to a thin dashed-pill placeholder reading
 * `[+] SHOW OPERATOR WORKSPACE GUIDE`. Clicking the placeholder restores
 * the full guide. The collapsed/expanded preference persists in
 * localStorage so it survives reloads.
 */
const OperatorGuide = () => {
  const [collapsed, setCollapsed] = useState(() => {
    if (typeof window === "undefined") return true;
    try {
      const stored = window.localStorage.getItem(STORAGE_KEY);
      // Default to collapsed for the new minimalist workspace.
      // Anything stored other than the explicit "0" stays collapsed.
      return stored !== "0";
    } catch {
      return true;
    }
  });

  const setPersisted = (next) => {
    setCollapsed(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next ? "1" : "0");
    } catch {
      /* storage blocked */
    }
  };

  if (collapsed) {
    return (
      <button
        type="button"
        data-testid="operator-guide-toggle"
        onClick={() => setPersisted(false)}
        className="w-full mb-6 group inline-flex items-center justify-center gap-2 border border-dashed border-white/[0.18] hover:border-[#FF0033]/60 bg-transparent hover:bg-[#FF0033]/[0.04] text-[#cbd5e1] hover:text-[#FF0033] font-mono text-[11px] uppercase tracking-[0.2em] py-3 transition-colors"
      >
        <BookOpen size={11} className="opacity-70 group-hover:opacity-100" />
        [+] Show Operator Workspace Guide
      </button>
    );
  }

  return (
    <aside
      data-testid="operator-guide"
      className="mb-6 border border-white/[0.06] bg-white/[0.02] chaos-reveal"
    >
      <div className="flex items-center justify-between gap-3 border-b border-white/[0.06] px-4 sm:px-5 py-2.5">
        <div className="flex items-center gap-2.5">
          <BookOpen size={12} className="text-[#FF0033]" />
          <span className="font-mono text-[10px] sm:text-[11px] uppercase tracking-[0.24em] text-[#cbd5e1]">
            Validation Operator Guide
          </span>
        </div>
        <button
          type="button"
          data-testid="operator-guide-dismiss"
          onClick={() => setPersisted(true)}
          aria-label="Collapse guide"
          className="w-6 h-6 flex items-center justify-center text-[#475569] hover:text-[#FF0033] transition-colors"
        >
          <X size={12} />
        </button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-white/[0.06]">
        <div className="px-4 sm:px-5 py-3.5">
          <div className="flex items-center gap-2 mb-1.5 text-[#FF0033]">
            <Sparkles size={12} />
            <span className="font-mono text-[10px] uppercase tracking-[0.22em]">
              01 · Frame the claim
            </span>
          </div>
          <p className="font-mono text-[12px] sm:text-[12.5px] font-medium text-[#e2e8f0] leading-relaxed">
            Phrase as a single statement or question. Best results start with
            “Is it true that…”, “Did X say Y…”, or paste the headline verbatim.
          </p>
        </div>
        <div className="px-4 sm:px-5 py-3.5">
          <div className="flex items-center gap-2 mb-1.5 text-[#FF0033]">
            <Workflow size={12} />
            <span className="font-mono text-[10px] uppercase tracking-[0.22em]">
              02 · Live cross-check
            </span>
          </div>
          <p className="font-mono text-[12px] sm:text-[12.5px] font-medium text-[#e2e8f0] leading-relaxed">
            One scan costs 2 credits and fires a Serper.dev web scan. The top 5 organic snippets
            are returned as cited evidence cards you can open directly.
          </p>
        </div>
        <div className="px-4 sm:px-5 py-3.5">
          <div className="flex items-center gap-2 mb-1.5 text-[#FF0033]">
            <Shield size={12} />
            <span className="font-mono text-[10px] uppercase tracking-[0.22em]">
              03 · AI verdict
            </span>
          </div>
          <p className="font-mono text-[12px] sm:text-[12.5px] font-medium text-[#e2e8f0] leading-relaxed">
            A frontier AI model reads the same snippets and emits a color-coded
            verdict + confidence score with reasoning. Refunded on any failure.
          </p>
        </div>
      </div>
    </aside>
  );
};

export default OperatorGuide;
